import { Button, CloseButton } from '@components/utility/buttons';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Box, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Typography } from '@mui/material';
import SerialNumberIdentification from '@rla/assets/images/identify-serial-number.gif';
import ProductIdentificationImage from '@rla/assets/images/product-identification.png';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export enum ProductIdentificationMode {
    LABEL = 'label',
    APP = 'app',
}

export type ProductIdentificationDialogRef = { setMode: (newMode: ProductIdentificationMode) => void };

type ProductIdentificationDialogProps = Omit<DialogProps, 'open' | 'onClose'>;

export const ProductIdentificationDialog = forwardRef<ProductIdentificationDialogRef, ProductIdentificationDialogProps>(
    (props, ref) => {
        const [open, setOpen] = useState(false);
        const [mode, setMode] = useState<ProductIdentificationMode | null>(null);
        const intl = useIntl();

        const handleDialogClose: NonNullable<DialogProps['onClose']> = (_event, _reason) => {
            setOpen(false);
        };

        useImperativeHandle(ref, () => ({
            setMode: (newMode: ProductIdentificationMode) => {
                setMode(newMode);
                setOpen(true);
            },
        }));

        return (
            <Dialog
                {...props}
                open={open}
                keepMounted
                slotProps={{
                    transition: {
                        onExited: () => {
                            setMode(null);
                        },
                    },
                    paper: {
                        sx: {
                            minWidth: 0,
                            maxWidth: 400,
                        },
                    },
                }}
                onClose={handleDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <CloseButton onClick={(event) => handleDialogClose(event, 'escapeKeyDown')} />
                <DialogTitle display="flex" alignItems="center" gap="18px">
                    <Box
                        width={50}
                        height={50}
                        borderRadius="5px"
                        bgcolor="neutral-100.main"
                        flexShrink={0}
                        className="black-border"
                        alignItems="center"
                        justifyContent="center"
                        display="flex"
                    >
                        <DescriptionOutlinedIcon sx={{ fontSize: 30 }} color="primary" />
                    </Box>
                    <Typography fontFamily="museo-sans" fontSize={18} fontWeight={600}>
                        <FormattedMessage
                            id="productIdentificationDialog.title"
                            defaultMessage="Product identification"
                        />
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {mode === ProductIdentificationMode.LABEL ? (
                        <>
                            <img
                                src={ProductIdentificationImage}
                                alt={intl.formatMessage({
                                    id: 'productIdentificationDialog.imageAlt',
                                    defaultMessage: 'Product identification image',
                                })}
                                className="rounded-lg"
                            />
                            <Typography fontSize={18} fontWeight={600} mt={2}>
                                <FormattedMessage
                                    id="productIdentificationDialog.howToIdentify.product"
                                    defaultMessage="How to Identify your product"
                                />
                            </Typography>
                            <Typography color="gray-600" fontSize={14} mt={1}>
                                <FormattedMessage
                                    id="productIdentificationDialog.howToIdentify.description.product"
                                    defaultMessage="To identify your product, please provide a clear, legible photograph of the product label. A sample label layout is shown above for your reference."
                                />
                            </Typography>
                        </>
                    ) : mode === ProductIdentificationMode.APP ? (
                        <>
                            <img
                                src={SerialNumberIdentification}
                                alt={intl.formatMessage({
                                    id: 'productIdentificationDialog.imageAlt.serialNumber',
                                    defaultMessage: 'Serial number identification image',
                                })}
                                className="rounded-lg"
                            />
                            <Typography fontSize={18} fontWeight={600} mt={2}>
                                <FormattedMessage
                                    id="productIdentificationDialog.howToIdentify.serialNumber"
                                    defaultMessage="How to Identify your Serial Number"
                                />
                            </Typography>
                            <Typography color="gray-600" fontSize={14} mt={1}>
                                <FormattedMessage
                                    id="productIdentificationDialog.howToIdentify.description.serialNumber"
                                    defaultMessage="You can find your serial number and other device details in your settings. Simply tap Product Settings in the top right corner, open the three dots (⋮) menu, and select Product Info."
                                />
                            </Typography>
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions
                    className="flex flex-col gap-[12px] black-border border-0 border-t p-[16px] pt-[24px] bg-[#F9FAFB]"
                    disableSpacing
                >
                    <Button fullRounded fullWidth onClick={(event) => handleDialogClose(event, 'escapeKeyDown')}>
                        <FormattedMessage id="common.iUnderstand" defaultMessage="I understand" />
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
);
